import React, {useEffect, useRef, useState} from 'react'
import PropTypes from 'prop-types'
import {APIProvider} from '@vis.gl/react-google-maps'
import {useMultiStyleConfig} from '@salesforce/retail-react-app/app/components/shared/ui'
import {useIntl} from 'react-intl'
import {useSearchStores} from '@salesforce/commerce-sdk-react'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import {getSiteEnvVarValue} from '../../../../../utils/site-config'
import {StoreLocatorProvider, useStoreLocator} from '../../../../../contexts/store-locator-context'
import {StoreLocatorStylesProvider} from '../../../../store-locator/styles/storeLocatorStyles'
import StoreLocatorMap from '../../../../store-locator/partials/store-locator-map'
import {STORE_LOCATOR_CONFIGS, getLocaleDefaults} from '../../../../store-locator/configs'
import {useBrand} from '../../../../../contexts/brand-context'

/**
 * Fetches stores near the directive's lat/lng via the Commerce SDK and
 * populates the StoreLocatorContext. Renders no UI.
 */
const StoreLoader = ({lat, lng}) => {
    const intl = useIntl()
    const {brandId} = useBrand()
    const config = getConfig()
    const {site} = useMultiSite()
    const siteId = site?.id
    const localeDefaults = getLocaleDefaults(intl.locale)
    const {setStores, setGeolocationLoading} = useStoreLocator()

    const {data: searchStoresData} = useSearchStores(
        {
            parameters: {
                countryCode: localeDefaults.countryCode,
                latitude: lat,
                longitude: lng,
                locale: intl.locale,
                maxDistance:
                    getSiteEnvVarValue(config?.app, 'STORELOCATOR_SEARCH_RADIUS', siteId) ||
                    STORE_LOCATOR_CONFIGS.STORE_LOCATOR_DISTANCE,
                limit: STORE_LOCATOR_CONFIGS.NUM_STORES_PER_REQUEST_API_MAX,
                distanceUnit:
                    getSiteEnvVarValue(config?.app, 'STORELOCATOR_DISTANCE_UNIT', siteId) ||
                    STORE_LOCATOR_CONFIGS.STORE_LOCATOR_DISTANCE_UNIT,
                c_brand: brandId
            }
        },
        {enabled: !!(lat && lng)}
    )

    // Mark geolocation as resolved immediately so StoreLocatorMap does not
    // wait for a permission check that will never happen.
    useEffect(() => {
        setGeolocationLoading(false)
    }, [])

    useEffect(() => {
        if (searchStoresData?.data?.length > 0) {
            setStores(searchStoresData.data)
        }
    }, [searchStoresData])

    return null
}

StoreLoader.propTypes = {lat: PropTypes.number, lng: PropTypes.number}

/**
 * Inner content — must live inside StoreLocatorProvider.
 * Loads stores near the directive coordinates, fixes the container span width,
 * and renders the full store locator map with markers.
 */
const GDIInlineMapContent = ({lat, lng, container}) => {
    const [mounted, setMounted] = useState(false)
    const styles = useMultiStyleConfig('StoreLocator', {})
    const {map} = useStoreLocator()

    // Keep a mutable ref so the ResizeObserver callback can trigger a Google
    // Maps resize without re-creating the observer on every map state change.
    const mapRef = useRef(null)
    mapRef.current = map

    // Fix container span width: when the <p> parent is a flex item that collapses
    // to 0 width, walk up to the first ancestor with a real width and copy it.
    // The ResizeObserver keeps this in sync on every window resize.
    useEffect(() => {
        if (!container) return

        let ancestor = container.parentElement
        while (ancestor && ancestor.offsetWidth === 0) ancestor = ancestor.parentElement

        const syncWidth = () => {
            if (ancestor && ancestor.offsetWidth > 0) {
                container.style.width = ancestor.offsetWidth + 'px'
            }
        }

        const raf = requestAnimationFrame(syncWidth)

        let observer = null
        if (ancestor && window.ResizeObserver) {
            observer = new ResizeObserver(() => {
                syncWidth()
                if (mapRef.current) {
                    window.google?.maps?.event?.trigger(mapRef.current, 'resize')
                }
            })
            observer.observe(ancestor)
        }

        return () => {
            cancelAnimationFrame(raf)
            observer?.disconnect()
        }
    }, [container])

    // Trigger a Google Maps resize once the instance is ready so that tiles
    // render at the correct container dimensions.
    useEffect(() => {
        if (!map) return
        const raf = requestAnimationFrame(() => {
            window.google?.maps?.event?.trigger(map, 'resize')
        })
        return () => cancelAnimationFrame(raf)
    }, [map])

    useEffect(() => {
        setMounted(true)
    }, [])

    if (!mounted) return null

    // Override only the map dimensions so the map fills the container span.
    // All other StoreLocator theme styles are inherited.
    const mapStyles = {
        ...styles,
        components: {
            ...styles?.components,
            map: {
                desktop: {width: '100%', height: '100%'},
                mobile: {width: '100%', height: '100%'}
            }
        }
    }

    return (
        <>
            <StoreLoader lat={lat} lng={lng} />
            <StoreLocatorStylesProvider value={mapStyles}>
                <StoreLocatorMap alwaysFitBounds={true} />
            </StoreLocatorStylesProvider>
        </>
    )
}

GDIInlineMapContent.propTypes = {
    lat: PropTypes.number,
    lng: PropTypes.number,
    container: PropTypes.object
}

/**
 * GDIInlineMap — renders the store-locator-themed map with store markers
 * inside a GDITextBlock via a React portal.
 * Mounted by gdiTextBlock when it detects a [data-gdi-map] span.
 *
 * @param {number}  lat       - Latitude for the store search center
 * @param {number}  lng       - Longitude for the store search center
 * @param {string}  apiKey    - Optional API key override; falls back to site config
 * @param {Element} container - The [data-gdi-map] span (portal target), used for
 *                              the fluid width fix.
 */
const GDIInlineMap = ({lat, lng, apiKey: apiKeyProp, container}) => {
    const config = getConfig()
    const {site} = useMultiSite()
    const apiKey = apiKeyProp || getSiteEnvVarValue(config?.app, 'GOOGLE_MAPS_API_KEY', site?.id) || null

    if (!apiKey) return null

    return (
        <APIProvider apiKey={apiKey}>
            <StoreLocatorProvider>
                <GDIInlineMapContent lat={lat} lng={lng} container={container} />
            </StoreLocatorProvider>
        </APIProvider>
    )
}

GDIInlineMap.propTypes = {
    lat: PropTypes.number,
    lng: PropTypes.number,
    apiKey: PropTypes.string,
    container: PropTypes.object
}

export default GDIInlineMap


